import * as THREE from 'three'
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'

import CharacterFSM from '../fsm/CharacterFSM'
import BasicCharacterControllerInput from './BasicCharacterControllerInput'
import BasicCharacterControllerProxy from './BasicCharacterControllerProxy'

import { hideLoader } from '../../utils/loader'

// Motion tuning — framerate-independent via exp-decay (lambda per second).
const ROT_LAMBDA = 9
const BANK_MAX = 0.45
const BANK_LAMBDA = 7
const SQUASH_LAMBDA = 10
const ACCEL = 70
const MAX_SPEED = 90
const DRAG = 2.5
const BREATHE_AMP = 0.025
const BREATHE_HZ = 0.7
const MIN_CAT_DIST = 7.5
const SEPARATION_K = 80

// Per-behavior "arrive" distance — cats stop approaching once within this.
// Picking a stop radius > MIN_CAT_DIST for socializing avoids clumping/oscillation.
const STOP_DIST = {
  wander: 1.5,
  play: 3.5,
  socialize: 9.0,
  rest: 0,
  pet: 0,
}

// Max radius on the XZ plane — keep cats inside the camera's view
const WORLD_BOUND = 42
const SPAWN_DURATION = 0.6
const BASE_SCALE = 4.5

const _zAxis = new THREE.Vector3(0, 0, 1)
const _forward = new THREE.Vector3()
const _toTarget = new THREE.Vector3()
const _delta = new THREE.Vector3()

// Class name preserved for external imports, but the behavior is now fully
// autonomous (no mouse-follow). It's the generic "CatAgent".
export default class MouseFollowCharacterController {
  constructor(params) {
    this.params = params // { camera, scene, path, world, genome, spawnPos }

    this.world = params.world || null
    this.genome = params.genome || { speed: 1, curiosity: 0.5, sociability: 0.5, playfulness: 0.5, restfulness: 0.3, size: 1 }
    this.spawnPos = params.spawnPos || new THREE.Vector3()

    this.velocity = new THREE.Vector3(0, 0, 0)
    this._position = new THREE.Vector3()

    // Motion flair
    this._prevYaw = 0
    this._angularVel = 0
    this._bank = 0
    this._squashX = 1
    this._squashY = 1
    this._squashZ = 1
    this._breatheT = Math.random() * Math.PI * 2
    this._spawnT = 0
    this._lastSpeed = 0
    this.feetOffset = 0

    // Behavior state
    this._behavior = null             // 'wander' | 'rest' | 'play' | 'socialize' | 'pet'
    this._behaviorT = 0               // seconds remaining
    this._behaviorTargetVec = null    // Vector3 target (static) or null for dynamic
    this._petT = 0                    // seconds of pet-mode remaining
    this._arrivalFired = false        // latch for per-trip arrival reward
    this.happiness = 50               // 0..100
    this.age = 0                      // seconds lived

    this.animations = {}
    this.currentAnimation = null
    this.collisionObjects = []
    this.input = new BasicCharacterControllerInput()
    this.stateMachine = new CharacterFSM(
      new BasicCharacterControllerProxy(this.animations)
    )

    this.loadModels()
  }

  get position() {
    return this._position
  }

  get rotation() {
    if (!this.target) return new THREE.Quaternion()
    return this.target.quaternion
  }

  get isMature() {
    return !!this.target && this._spawnT >= SPAWN_DURATION
  }

  get fitness() {
    // Simple fitness — happy + long-lived cats are preferred for saving
    return this.happiness * 0.5 + this.age * 0.3
  }

  loadModels() {
    const modelName = 'cat_compressed.glb'
    const isFBX = modelName.includes('fbx')
    const loadingManager = new THREE.LoadingManager()
    const dracoLoader = new DRACOLoader(loadingManager)
    dracoLoader.setDecoderConfig({ type: 'js' })
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/')
    dracoLoader.preload()
    const loader = new GLTFLoader(loadingManager)
    loader.setDRACOLoader(dracoLoader)

    const loadingText = document.getElementById('model-loader-text')
    loadingManager.onStart = () => {
      if (loadingText) loadingText.innerHTML = 'preparing feline assets…'
    }
    loadingManager.onLoad = () => {
      const el = document.getElementById('model-loader-container')
      if (el) el.style.display = 'none'
    }
    loader.setPath(this.params.path)

    loader.load(modelName, (loaderObj) => {
      const model = loaderObj?.scale ? loaderObj : loaderObj.scene

      // Measure feet offset at scale=1 so we can scale the lift with the cat's
      // current scale factor (genome.size + spawn pop + happy boost + squash).
      model.scale.setScalar(1)
      model.updateMatrixWorld(true)
      const box = new THREE.Box3().setFromObject(model)
      this.feetOffsetUnit = -box.min.y   // lift (in local space) per unit of scale
      model.scale.setScalar(0)           // invisible — spawn pop will grow it

      model.traverse((child) => { child.castShadow = true })

      // Rig hierarchy:
      //   rig (position, yaw) → bankPivot (roll around feet) → model (lift+scale)
      // Banking at a pivot that sits at y=0 keeps the feet locked to the ground
      // regardless of roll angle, genome.size, or spawn pop.
      const rig = new THREE.Group()
      const bankPivot = new THREE.Group()
      rig.position.set(this.spawnPos.x, 0, this.spawnPos.z)
      rig.add(bankPivot)
      bankPivot.add(model)

      this.target = rig
      this.bankPivot = bankPivot
      this.model = model
      this._position.copy(rig.position)

      this.params.scene.add(rig)
      this.mixer = new THREE.AnimationMixer(model)

      this.manager = new THREE.LoadingManager()
      this.manager.onLoad = () => this.stateMachine.setState('idle')

      const onLoadAnimation = (name, data) => {
        const clip = data.animations[0]
        const action = this.mixer.clipAction(clip)
        this.animations[name] = { clip, action }
      }

      const loaderWithManager = isFBX
        ? new FBXLoader(this.manager)
        : new GLTFLoader(this.manager)
      loaderWithManager.setDRACOLoader(dracoLoader)
      loaderWithManager.setPath(this.params.path)

      loaderWithManager.load(modelName, (data) => {
        for (const anim of data.animations) {
          const animName = anim.name
            .toLowerCase()
            .replace('fatcat_rig|fatcat_rig|', '')
            .split(' ')[0]
          onLoadAnimation(animName, { animations: [anim] })
        }
        hideLoader()
      })
    })
  }

  // -----------------------------------------------------------
  // User-facing interaction
  // -----------------------------------------------------------
  pet() {
    this._petT = 3.0
    this.happiness = Math.min(100, this.happiness + 28)
    this._behavior = 'pet'
    this._behaviorT = 3.0
    if (this.animations['jump']) this.playAndLoopbackAnimation('jump')
  }

  // -----------------------------------------------------------
  // Brain
  // -----------------------------------------------------------
  _pickBehavior() {
    const g = this.genome
    const world = this.world

    // Pet overrides everything
    if (this._petT > 0) {
      this._behavior = 'pet'
      this._behaviorT = this._petT
      this._behaviorTargetVec = null
      return
    }

    const ballPos = world?.ball?.position
    const ballDist = ballPos ? this._position.distanceTo(ballPos) : Infinity
    const nearestFriend = this._findNearestCat()
    const friendDist = nearestFriend ? this._position.distanceTo(nearestFriend._position) : Infinity

    // Weight behaviors by genome + environment proximity.
    // Playful cats chase the ball wherever it goes (weight doesn't drop off
    // with distance); sociable cats still prefer nearby friends.
    const weights = {
      wander: 0.5 + g.curiosity * 0.5,
      rest: g.restfulness * 1.3,
      play: g.playfulness * 3.2,
      socialize: g.sociability * (friendDist < 40 ? 3.0 : 0.5),
    }

    const total = Object.values(weights).reduce((a, b) => a + b, 0) || 1
    let roll = Math.random() * total
    let picked = 'wander'
    for (const [name, w] of Object.entries(weights)) {
      roll -= w
      if (roll <= 0) { picked = name; break }
    }

    this._behavior = picked
    // Shorter behavior cycles = more observable change
    this._behaviorT = picked === 'rest' ? (2 + Math.random() * 3) : (2.5 + Math.random() * 3)

    if (picked === 'wander') {
      // Smaller hops; clamp globally to WORLD_BOUND so cats stay visible
      const radius = 6 + g.curiosity * 18
      const t = Math.random() * Math.PI * 2
      this._behaviorTargetVec = new THREE.Vector3(
        this._position.x + Math.cos(t) * radius,
        0,
        this._position.z + Math.sin(t) * radius
      )
      const rFromOrigin = Math.hypot(this._behaviorTargetVec.x, this._behaviorTargetVec.z)
      if (rFromOrigin > WORLD_BOUND) {
        this._behaviorTargetVec.multiplyScalar(WORLD_BOUND / rFromOrigin)
      }
    } else {
      this._behaviorTargetVec = null
    }
  }

  _resolveTarget() {
    if (this._behavior === 'play' && this.world?.ball) {
      return this.world.ball.position
    }
    if (this._behavior === 'socialize') {
      const friend = this._findNearestCat()
      if (friend) return friend._position
    }
    return this._behaviorTargetVec // null for rest/pet, vec for wander
  }

  _findNearestCat() {
    let nearest = null
    let nd = Infinity
    for (const other of this.collisionObjects) {
      if (other === this || !other._position || !other.isMature) continue
      const d = this._position.distanceTo(other._position)
      if (d < nd) { nd = d; nearest = other }
    }
    return nearest
  }

  // -----------------------------------------------------------
  // Animation helpers (unchanged from prior version)
  // -----------------------------------------------------------
  playAnimation(name) {
    if (this.currentAnimation === name || !this.animations[name]) return
    const currentAction = this.animations[name].action
    if (this.currentAnimation && this.animations[this.currentAnimation]) {
      const prevAction = this.animations[this.currentAnimation].action
      if (currentAction && prevAction) {
        currentAction.reset()
        currentAction.clampWhenFinished = true
        currentAction.crossFadeFrom(prevAction, 0.2, true)
        currentAction.play()
        this.currentAnimation = name
      }
    } else if (currentAction) {
      this.currentAnimation = name
      currentAction.play()
    }
  }

  playAndLoopbackAnimation(name) {
    if (this.currentAnimation === name || !this.animations[name]) return
    const currentAction = this.animations[name].action
    if (!currentAction) return
    const mixer = currentAction.getMixer()
    const onFinished = () => {
      mixer.removeEventListener('finished', onFinished)
      this.playAnimation('idle')
    }
    mixer.addEventListener('finished', onFinished)

    if (this.currentAnimation && this.animations[this.currentAnimation]) {
      const prevAction = this.animations[this.currentAnimation].action
      if (prevAction) {
        currentAction.reset()
        currentAction.setLoop(THREE.LoopPingPong, 1)
        currentAction.clampWhenFinished = true
        currentAction.crossFadeFrom(prevAction, 0.2, true)
        currentAction.play()
        this.currentAnimation = name
      }
    } else {
      this.currentAnimation = name
      currentAction.setLoop(THREE.LoopPingPong, 1)
      currentAction.play()
    }
  }

  _setTimeScale(name, scale) {
    const a = this.animations[name]?.action
    if (a) a.timeScale = scale
  }

  // -----------------------------------------------------------
  // Update — physics, behavior, animation
  // -----------------------------------------------------------
  update(dt) {
    if (!this.target) return
    const alpha = (lambda) => 1 - Math.exp(-lambda * Math.max(dt, 0))

    this.stateMachine.update(dt, this.input)

    this._spawnT = Math.min(SPAWN_DURATION, this._spawnT + dt)
    this._behaviorT -= dt
    this._petT = Math.max(0, this._petT - dt)
    this.happiness = Math.max(0, this.happiness - dt * 1.2)
    this.age += dt

    if (this._behavior === null || this._behaviorT <= 0) {
      this._pickBehavior()
      this._arrivalFired = false // new trip
    }
    const controlObject = this.target
    const targetPos = this._resolveTarget()

    // --- 1) Facing rotation toward target (if any) ---
    let distance = 0
    let forwardDot = 0
    const stopDist = STOP_DIST[this._behavior] ?? 1.5
    if (targetPos) {
      _toTarget.subVectors(targetPos, controlObject.position)
      _toTarget.y = 0
      distance = _toTarget.length()
      if (distance > 1e-4) {
        const dir = _toTarget.clone().multiplyScalar(1 / distance)

        // Skip rotation once we're inside the approach zone — prevents the
        // circular-pursuit spiral where two cats chase each other's moving
        // positions forever.
        if (distance > stopDist * 1.2) {
          const facingQ = new THREE.Quaternion().setFromUnitVectors(_zAxis, dir)
          controlObject.quaternion.slerp(facingQ, alpha(ROT_LAMBDA))
        }

        const yaw = Math.atan2(
          2 * (controlObject.quaternion.w * controlObject.quaternion.y),
          1 - 2 * (controlObject.quaternion.y * controlObject.quaternion.y)
        )
        let dYaw = yaw - this._prevYaw
        if (dYaw > Math.PI) dYaw -= Math.PI * 2
        if (dYaw < -Math.PI) dYaw += Math.PI * 2
        this._angularVel = dt > 0 ? dYaw / dt : 0
        this._prevYaw = yaw

        _forward.set(0, 0, 1).applyQuaternion(controlObject.quaternion)
        forwardDot = _forward.dot(dir)
      }
    }

    // --- 2) Drive speed based on behavior ---
    const wantsToMove =
      !!targetPos &&
      this._behavior !== 'rest' &&
      this._behavior !== 'pet' &&
      distance > stopDist &&
      forwardDot > 0.2

    if (wantsToMove) {
      // Different behaviors move at different speeds
      let speedMul = this.genome.speed
      if (this._behavior === 'play') speedMul *= 1.1       // chase energetically
      if (this._behavior === 'socialize') speedMul *= 0.7  // casual approach
      if (this._behavior === 'wander') speedMul *= 0.75    // saunter
      const maxSpeed = MAX_SPEED * speedMul
      // Ease desired speed down near the stop zone so we don't overshoot
      const approachRange = Math.max(0, distance - stopDist)
      const desired = Math.min(maxSpeed, Math.max(0, approachRange * 6))
      const diff = desired - this.velocity.z
      this.velocity.z += THREE.MathUtils.clamp(diff, -ACCEL * dt, ACCEL * dt)
    } else {
      // Heavier drag when we've arrived so we don't keep drifting into neighbors
      this.velocity.z *= Math.exp(-DRAG * 2.5 * dt)
      if (Math.abs(this.velocity.z) < 0.05) this.velocity.z = 0
    }

    // --- 3) Move position ---
    const nextPos = controlObject.position.clone()
    if (this.velocity.z > 0.01) {
      _forward.set(0, 0, 1).applyQuaternion(controlObject.quaternion)
      nextPos.add(_forward.multiplyScalar(this.velocity.z * dt))
    }

    // --- 4) Soft separation from neighbors ---
    if (this.collisionObjects.length > 1) {
      const push = new THREE.Vector3()
      for (const other of this.collisionObjects) {
        if (other === this || !other._position) continue
        _delta.subVectors(nextPos, other._position)
        _delta.y = 0
        const d = _delta.length()
        if (d > 1e-3 && d < MIN_CAT_DIST) {
          const overlap = (MIN_CAT_DIST - d) / MIN_CAT_DIST
          push.add(_delta.multiplyScalar(overlap * SEPARATION_K / d))
        }
      }
      if (push.lengthSq() > 0) {
        nextPos.add(push.multiplyScalar(dt))
        this.velocity.z *= Math.exp(-2 * dt)
      }
    }

    // --- 5) Commit position (rig stays at y=0; bankPivot pivots around feet) ---
    // No hard positional clamp — cats may follow the ball wherever. Wander
    // targets are still clamped to WORLD_BOUND so idle cats stay in view.
    controlObject.position.copy(nextPos)
    controlObject.position.y = 0
    this._position.copy(controlObject.position)

    // --- 5c) Arrival rewards — trigger interactions when we reach the target ---
    const justArrived = !!targetPos && distance > 0 && distance <= stopDist + 0.6 && this.velocity.z < 1.5
    if (justArrived && !this._arrivalFired) {
      this._arrivalFired = true
      if (this._behavior === 'socialize') {
        this.happiness = Math.min(100, this.happiness + 12)
        if (Math.random() < 0.35 + this.genome.playfulness * 0.5 && this.animations['jump']) {
          this.playAndLoopbackAnimation('jump')
        }
        // End early so we disperse rather than loiter
        this._behavior = 'rest'
        this._behaviorT = 2 + Math.random() * 2
      } else if (this._behavior === 'play' && this.world?.ball) {
        this.happiness = Math.min(100, this.happiness + 10)
        if (Math.random() < 0.6 && this.animations['jump']) {
          this.playAndLoopbackAnimation('jump')
        }
        // Nudge the ball away — playful swat (no world clamp, user decides)
        const away = new THREE.Vector3()
          .subVectors(this.world.ball.position, this._position)
          .setY(0)
        if (away.lengthSq() > 0) {
          away.normalize().multiplyScalar(10 + this.genome.playfulness * 10)
          const newBall = this.world.ball.position.clone().add(away)
          newBall.y = 0
          this.world.moveBall(newBall)
        }
        // Pick a new behavior next frame
        this._behavior = 'rest'
        this._behaviorT = 1 + Math.random() * 1.5
      }
    }
    if (distance > stopDist + 2) this._arrivalFired = false

    // --- 6) Banking — rotate the bank pivot around cat's local forward Z ---
    const targetBank = THREE.MathUtils.clamp(
      -this._angularVel * 0.35,
      -BANK_MAX,
      BANK_MAX
    )
    this._bank = THREE.MathUtils.lerp(this._bank, targetBank, alpha(BANK_LAMBDA))
    if (this.bankPivot) {
      this.bankPivot.quaternion.setFromAxisAngle(_zAxis, this._bank)
    }

    // --- 7) Squash/stretch from speed derivative ---
    const accelSignal = (this.velocity.z - this._lastSpeed) / Math.max(dt, 1e-3)
    this._lastSpeed = this.velocity.z
    const targetSqY = 1 - THREE.MathUtils.clamp(accelSignal / 200, -0.12, 0.12)
    const targetSqX = 1 + THREE.MathUtils.clamp(accelSignal / 300, -0.08, 0.08)
    this._squashY = THREE.MathUtils.lerp(this._squashY, targetSqY, alpha(SQUASH_LAMBDA))
    this._squashX = THREE.MathUtils.lerp(this._squashX, targetSqX, alpha(SQUASH_LAMBDA))
    this._squashZ = this._squashX

    // --- 8) Idle breathing when nearly stopped ---
    this._breatheT += dt
    const idleFactor = 1 - THREE.MathUtils.clamp(this.velocity.z / 20, 0, 1)
    const breathe = Math.sin(this._breatheT * Math.PI * 2 * BREATHE_HZ) * BREATHE_AMP * idleFactor

    const happy = this.happiness / 100
    const happyBoost = 1 + happy * 0.02

    // --- 9) Spawn pop-in (back-out overshoot) ---
    const spawnK = this._spawnEase(this._spawnT / SPAWN_DURATION)

    // --- 10) Final scale apply (on the model, not the rig) ---
    const s = BASE_SCALE * this.genome.size * spawnK * happyBoost
    const sX = s * this._squashX
    const sY = s * (this._squashY + breathe)
    const sZ = s * this._squashZ
    if (this.model) {
      this.model.scale.set(sX, sY, sZ)
      // Lift the model so its feet sit at bankPivot origin (world y=0).
      // Lift tracks the ACTUAL Y scale so breathing/squash don't poke through.
      this.model.position.y = this.feetOffsetUnit * sY
    }

    // --- 12) Animation selection ---
    if (Object.keys(this.animations).length > 0) {
      if (this._behavior === 'pet') {
        // handled by playAndLoopbackAnimation('jump') in pet(), falls back to idle
      } else {
        const speed = this.velocity.z
        if (speed > 40) {
          this.playAnimation('run')
          this._setTimeScale('run', THREE.MathUtils.clamp(0.75 + speed / 120, 0.8, 1.6))
        } else if (speed > 1.5) {
          this.playAnimation('walk')
          this._setTimeScale('walk', THREE.MathUtils.clamp(0.6 + speed / 60, 0.7, 1.7))
        } else if (
          this.currentAnimation !== 'jump' &&
          this.currentAnimation !== 'roll'
        ) {
          this.playAnimation('idle')
          if (this._behavior === 'rest' && Math.random() < 0.0005) {
            this.playAndLoopbackAnimation('jump')
          }
        }
      }
    }

    this.mixer?.update(dt)
  }

  _spawnEase(t) {
    t = Math.min(1, Math.max(0, t))
    const c1 = 1.70158
    const c3 = c1 + 1
    const u = t - 1
    return 1 + c3 * u * u * u + c1 * u * u
  }
}
